<template>
  <span
    ref="tooltipIcon"
    :class="{
      'tooltip-fi': true,
      'tooltip-fi--disabled': disabled,
    }"
    @mouseover="onMouseOver"
    @mouseleave="onMouseLeave"
    @click.prevent.stop="toggleTooltip"
  >
    <slot name="trigger">
      <icon-fi
        class="tooltip-fi__icon"
        icon="info"
      />
    </slot>

    <transition
      enter-active-class="animated fadeIn faster"
      leave-active-class="animated fadeOut faster"
    >
      <div
        v-if="!isSM && showTooltip && !disabled"
        class="tooltip-fi__overlay"
      />
    </transition>

    <teleport
      v-if="wasOpened"
      to="body"
    >
      <component-wrapper>
        <transition
          :enter-active-class="
            isSM ? 'animated fadeIn faster' : 'animated slideInFromBottom fastest'
          "
          :leave-active-class="
            isSM ? 'animated fadeOut faster' : 'animated slideOutToBottom fastest'
          "
        >
          <div
            v-if="showTooltip && !disabled"
            ref="tooltipRef"
            :style="tooltipStyle"
            :area-hidden="!showTooltip"
            :class="{
              'tooltip-fi__flyout': true,
              'tooltip-fi__flyout--wide': wide,
              [customClass]: customClass,
            }"
            @mouseover="onMouseOverTooltip"
            @mouseleave="onMouseLeave"
          >
            <div
              :style="tooltipArrowStyle"
              class="tooltip-fi__flyout__arrow"
            />
            <div class="tooltip-fi__close">
              <icon-fi
                icon="close"
                @click="closeTooltip"
              />
            </div>
            <!-- eslint-disable vue/no-v-html -->
            <div class="tooltip-fi__flyout__content">
              <slot>
                <!-- eslint-disable-next-.ine vue/no-v-html -->
                <div v-html="tooltip" />
              </slot>
            </div>
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </transition>
      </component-wrapper>
    </teleport>
  </span>
</template>

<script setup lang="ts">
import { debounce } from 'throttle-debounce';
import { computed, nextTick, reactive, ref, useTemplateRef, watch } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import { useBreakpoints } from '@/composables/breakpoint';

import IconFi from '../icon-fi/IconFi.vue';
import { useTooltipFiStore } from './tooltipFiStore';

const props = withDefaults(
  defineProps<{
    tooltip?: string;
    customClass?: string;
    disabled?: boolean;
    wide?: boolean;
    toRight?: boolean;
  }>(),
  {
    tooltip: '',
    customClass: '',
    disabled: false,
    wide: false,
  },
);

const { isSM } = useBreakpoints();

const tooltipStyle = reactive<Record<string, string>>({});
const tooltipArrowStyle = reactive<Record<string, string>>({});
const wasOpened = ref(false);
const tooltipFiStore = useTooltipFiStore();

const tooltipIconRef = useTemplateRef<HTMLSpanElement>('tooltipIcon');
const tooltipRef = useTemplateRef<HTMLDivElement>('tooltipRef');

const tooltipId = tooltipFiStore.getId();

const closeTooltip = () => {
  if (showTooltip.value) {
    tooltipFiStore.open = null;
  }
};

const toggleTooltip = () => {
  if (showTooltip.value) {
    closeTooltip();
  } else {
    openTooltip();
  }
};

async function openTooltip() {
  if (IS_SSR) {
    return;
  }
  if (props.disabled) {
    closeTooltip();
    return;
  }

  if (showTooltip.value) {
    return;
  }

  if (!tooltipIconRef.value) {
    console.error('Missing ref for tooltip');
    return;
  }

  wasOpened.value = true;
  if (isSM) {
    await nextTick();
  }
  tooltipFiStore.open = tooltipId;

  if (!isSM.value) {
    delete tooltipStyle.top;
    delete tooltipStyle.left;
    return;
  }

  await nextTick();

  const width = props.wide ? 800 : 300;
  const screenMargin = 10;
  const rotationOffset = 10; // rotating the arrow 'moves' it half its width
  const arrowTipRight = 50; // position of the arrow from the right of the bubble

  const clientRect = tooltipIconRef.value.getBoundingClientRect();
  const documentWidth = window.document.body.clientWidth;

  const tooltipLeft = Math.min(
    Math.max(screenMargin, clientRect.left - width + arrowTipRight + rotationOffset),
    Math.max(documentWidth - width - screenMargin, screenMargin),
  );

  if (props.toRight && clientRect.x + width < documentWidth) {
    tooltipStyle.top = `${clientRect.top}px`;
    tooltipStyle.left = `${clientRect.right + 8}px`;
    tooltipStyle.transform = 'unset';
    delete tooltipArrowStyle.bottom;
    tooltipArrowStyle.top = '13px';
    tooltipArrowStyle.left = '-8px';
    return;
  }

  tooltipStyle.top = `${clientRect.top - 8}px`;
  tooltipStyle.left = `${tooltipLeft}px`;
  tooltipArrowStyle.left = `${clientRect.left - tooltipLeft + clientRect.width / 2 - rotationOffset}px`;

  await nextTick();

  const tooltipRect = tooltipRef.value?.getBoundingClientRect();
  if (!tooltipRect) {
    console.error('got no tooltip ref');
    return;
  }

  if (tooltipRect.top < 0) {
    tooltipStyle.top = `${clientRect.top + tooltipRect.height + 44}px`;
    tooltipArrowStyle.bottom = 'unset';
    tooltipArrowStyle.top = '-8px';
  }
}

var isHovered = false;
const mouseEnterDebounce = debounce(400, () => {
  if (!isHovered) {
    return;
  }
  if (isSM.value) {
    openTooltip();
  }
});
const mouseLeaveDebounce = debounce(800, () => {
  if (isHovered) {
    return;
  }
  if (isSM.value) {
    closeTooltip();
  }
});

const onMouseOver = () => {
  isHovered = true;
  mouseEnterDebounce();
};
const onMouseOverTooltip = () => {
  // ignore if already closing
  if (showTooltip.value) {
    onMouseOver();
  }
};

const onMouseLeave = () => {
  isHovered = false;
  mouseLeaveDebounce();
};

watch(isSM, (value) => {
  if (!value && showTooltip.value) {
    closeTooltip();
  }
});

const showTooltip = computed(() => tooltipFiStore.open === tooltipId);
</script>

<style scoped src="./tooltip-fi" />
